import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink, ArrowRight } from 'lucide-react';
import { COMPANY_INFO } from '../data';

export default function Navigation() {
  const [isCatalogDropdownOpen, setIsCatalogDropdownOpen] = useState(false);
  const location = useLocation();

  // Luk dropdown ved ruteskift
  useEffect(() => {
    setIsCatalogDropdownOpen(false);
  }, [location]);

  // Luk dropdown ved klik udenfor
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.catalog-dropdown-container')) {
        setIsCatalogDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const catalogLinks = [
    { label: 'Se hele produktkataloget', href: 'https://vesthimmerlands.brukerweb.no/product/index/', is_external: true, is_main: true },
    { label: 'Liftudlejning', to: '/liftudlejning', is_internal: true },
    { label: 'Bomlifte', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=93', is_external: true },
    { label: 'Saxlifte', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=25', is_external: true },
    { label: 'Minikran', to: '/minikran', is_internal: true, badge: 'Ny side' },
    { label: 'Kraner', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=306', is_external: true },
    { label: 'Gravemaskiner', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=20', is_external: true },
    { label: 'Læssemaskiner', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=26', is_external: true },
    { label: 'Minilæsser', to: '/minilaesser', is_internal: true },
    { label: 'Dumpere', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=16', is_external: true },
    { label: 'Komprimering', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=28', is_external: true },
    // Flere kategorier
    { label: 'Truck', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=32', is_external: true, is_sub: true },
    { label: 'Trailer', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=31', is_external: true, is_sub: true },
    { label: 'Letvogne', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=24', is_external: true, is_sub: true },
    { label: 'Container', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=13', is_external: true, is_sub: true },
    { label: 'Køreplader', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=23', is_external: true, is_sub: true },
    { label: 'Stilladser', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=29', is_external: true, is_sub: true },
    { label: 'Pumper', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=27', is_external: true, is_sub: true },
    { label: 'Affugter', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=10', is_external: true, is_sub: true },
    { label: 'Varmeblæsere / ventilation', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=35', is_external: true, is_sub: true },
    { label: 'Generator / lystårne', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=17', is_external: true, is_sub: true },
    { label: 'Bygningsmateriel', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=12', is_external: true, is_sub: true },
    { label: 'Dieseltanke', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=15', is_external: true, is_sub: true },
    { label: 'Grønt materiel', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=21', is_external: true, is_sub: true },
    { label: 'Gravekasse', href: 'https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=19', is_external: true, is_sub: true }
  ];

  const mainCatalogLinks = catalogLinks.filter(item => !item.is_sub && !item.is_main);
  const subCatalogLinks = catalogLinks.filter(item => item.is_sub);
  const mainLink = catalogLinks.find(item => item.is_main);

  return (
    <div className="w-full bg-[#bb0020] shadow-md">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <nav className="relative flex items-center justify-center gap-x-5 sm:gap-x-8 gap-y-3 py-3.5 w-full catalog-dropdown-container">
          {/* Forside */}
          <NavLink
            to="/"
            className={({ isActive }) =>
              `text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-200 pb-1 border-b-2 ${
                isActive
                  ? 'border-white text-white'
                  : 'border-transparent text-white/90 hover:text-white hover:border-white/40'
              }`
            }
          >
            Forside
          </NavLink>

          {/* Produktkatalog Dropdown */}
          <div className="relative group">
            {/* Desktop Knap (Hover) */}
            <button
              className="hidden md:flex items-center gap-1 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-200 pb-1 border-b-2 border-transparent text-white/90 hover:text-white hover:border-white/40 cursor-pointer"
            >
              <span>Produktkatalog</span>
              <ChevronDown className="h-4 w-4 text-white/70 group-hover:text-white transition-colors" />
            </button>

            {/* Mobil Knap (Klik) */}
            <button
              onClick={() => setIsCatalogDropdownOpen(!isCatalogDropdownOpen)}
              className="md:hidden flex items-center gap-1 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-200 pb-1 border-b-2 border-transparent text-white/90 hover:text-white hover:border-white/40 cursor-pointer"
            >
              <span>Produktkatalog</span>
              {isCatalogDropdownOpen ? (
                <ChevronUp className="h-4 w-4 text-white" />
              ) : (
                <ChevronDown className="h-4 w-4 text-white/70" />
              )}
            </button>

            {/* Desktop Dropdown Menu (Hover-baseret) */}
            <div className="hidden md:block">
              <div className="absolute left-1/2 -translate-x-1/2 pt-4 w-[680px] z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                <div className="bg-white border border-brand-grey rounded-2xl shadow-xl p-6 grid grid-cols-2 gap-6 text-left">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-brand-yellow mb-3 border-b border-gray-100 pb-2">
                      Populære Kategorier
                    </h4>
                    <div className="flex flex-col gap-1">
                      {mainCatalogLinks.map((item, idx) => (
                        item.is_internal ? (
                          <Link
                            key={idx}
                            to={item.to}
                            className="group/item flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-gray-700 hover:text-brand-yellow hover:bg-brand-light transition-all duration-150"
                          >
                            <div className="flex items-center gap-2">
                              <span>{item.label}</span>
                              {item.badge && (
                                <span className="rounded bg-brand-yellow/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-yellow">
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 text-brand-yellow opacity-0 -translate-x-2 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-150" />
                          </Link>
                        ) : (
                          <a
                            key={idx}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group/item flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-gray-700 hover:text-brand-yellow hover:bg-brand-light transition-all duration-150"
                          >
                            <span>{item.label}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-brand-yellow opacity-0 -translate-x-2 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-150" />
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 border-b border-gray-100 pb-2">
                      Flere Kategorier
                    </h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {subCatalogLinks.map((item, idx) => (
                        <a
                          key={idx}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-brand-yellow hover:bg-brand-light transition-all duration-150 block truncate"
                          title={item.label}
                        >
                          {item.label}
                        </a>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2 border-t border-gray-100 pt-4 mt-2 flex justify-between items-center">
                    <a
                      href={mainLink?.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover transition-colors"
                    >
                      <span>{mainLink?.label || 'Se hele produktkataloget'}</span>
                      <ArrowRight className="h-4 w-4" />
                    </a>
                    <span className="text-xs text-gray-400">Direkte online adgang</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lejebetingelser */}
          <a
            href={COMPANY_INFO.lejebetingelserUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs sm:text-sm font-bold uppercase tracking-wider text-white/90 hover:text-white hover:border-white/40 transition-all duration-200 pb-1 border-b-2 border-transparent"
          >
            <span>Lejebetingelser</span>
            <ExternalLink className="h-3.5 w-3.5 text-white/70" />
          </a>

          {/* Bliv kunde */}
          <NavLink
            to="/bliv-kunde"
            className={({ isActive }) =>
              `text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-200 pb-1 border-b-2 ${
                isActive
                  ? 'border-white text-white'
                  : 'border-transparent text-white/90 hover:text-white hover:border-white/40'
              }`
            }
          >
            Bliv kunde
          </NavLink>

          {/* Kontakt */}
          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-200 pb-1 border-b-2 ${
                isActive
                  ? 'border-white text-white'
                  : 'border-transparent text-white/90 hover:text-white hover:border-white/40'
              }`
            }
          >
            Kontakt
          </NavLink>

          {/* Mobil Dropdown Menu (Absolut i forhold til nav-baren) */}
          <div className="md:hidden">
            {isCatalogDropdownOpen && (
              <div className="absolute left-4 right-4 top-full mt-2 bg-white border border-brand-grey rounded-2xl shadow-xl p-4 z-50 flex flex-col gap-1 text-left max-h-[350px] overflow-y-auto">
                <a
                  href={mainLink?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between rounded-md bg-brand-yellow px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors mb-2"
                >
                  <span>{mainLink?.label}</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
                
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-1">
                  Populære Kategorier
                </div>
                {mainCatalogLinks.map((item, idx) => (
                  item.is_internal ? (
                    <Link
                      key={idx}
                      to={item.to}
                      className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-brand-yellow bg-brand-yellow/5 border border-brand-yellow/20 hover:bg-brand-light transition-all duration-150"
                    >
                      <div className="flex items-center gap-2">
                        <span>{item.label}</span>
                        {item.badge && (
                          <span className="rounded bg-brand-yellow text-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-brand-yellow" />
                    </Link>
                  ) : (
                    <a
                      key={idx}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-gray-700 hover:text-brand-yellow hover:bg-brand-light transition-all duration-150"
                    >
                      <span>{item.label}</span>
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                    </a>
                  )
                ))}

                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-1 border-t border-gray-200 mt-2 pt-2">
                  Flere Kategorier
                </div>
                {subCatalogLinks.map((item, idx) => (
                  <a
                    key={idx}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-gray-600 hover:text-brand-yellow hover:bg-brand-light transition-all duration-150"
                  >
                    <span>{item.label}</span>
                    <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
